import React from 'react'
import styled from 'styled-components'

import { color } from 'styles/colors'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

type LinearGradientColors = {
  topLeftColor: string
  fiftyPercentColor: string
  bottomRightColor: string
}

type FeatureCardProps = {
  isNew: boolean
  cardColors: LinearGradientColors
  cardTitle: string
  cardText: (string | string[])[]
  mainAccentColor: string
  buttonText: string
  onPress: () => void
  imageSrc: string
  imageAltText: string
  imageHeight?: number
}

const FeatureCard: React.FC<FeatureCardProps> = ({ isNew, cardColors, cardTitle, cardText, mainAccentColor, buttonText, onPress, imageSrc, imageAltText, imageHeight }) => {
  const learnMoreButtonStyle = {
    button: {
      background: mainAccentColor,
      borderRadius: 20,
      borderColor: mainAccentColor,
      paddingTop: 8,
      paddingBottom: 8,
      paddingLeft: 16,
      paddingRight: 16,
      marginTop: 50
    },
    text: {
      color: color('primaryButtonText'),
      fontWeight: 600,
      textTransform: 'none',
      fontSize: 14
    }
  } as ButtonStyles

  return (
    <>
      {isNew && <S.NewText $mainAccentColor={mainAccentColor}>New!</S.NewText>}
      <S.HehmContainer $cardColors={cardColors} $mainAccentColor={mainAccentColor}>
        <S.ContentWrapper>
          <S.ProductLogo src={imageSrc} alt={imageAltText} $imageHeight={imageHeight} />
          {cardText.map((item) => {
            if (Array.isArray(item)) {
              return (
                <S.BulletList key={`${cardTitle}-${item[0].substring(0, 20)}`}>
                  {item.map((bullet) => (
                    <S.BulletItem key={`${cardTitle}-${bullet.substring(0, 20)}`}>
                      {bullet}
                    </S.BulletItem>
                  ))}
                </S.BulletList>
              )
            } else {
              return <S.HehmDescription key={`${cardTitle}-${item.substring(0, 20)}`}>{item}</S.HehmDescription>
            }
          })}
        </S.ContentWrapper>
        <Button
          decoration="secondary"
          text={buttonText}
          aria-label={`learn more about ${cardTitle.toLowerCase()}`}
          onPress={onPress}
          styles={learnMoreButtonStyle}
        />
      </S.HehmContainer>
    </>
  )
}

export default FeatureCard

const S = {
  NewText: styled.span<{ $mainAccentColor: string }>`
    font-size: 18px;
    font-weight: bold;
    color: ${props => props.$mainAccentColor};
    margin-bottom: 5px;
    margin-top: -32px;
  `,
  HehmContainer: styled.div<{ $cardColors: LinearGradientColors, $mainAccentColor: string }>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 300px;
    border: 1px solid ${props => props.$mainAccentColor};
    border-radius: 16px;
    padding: 20px;
    background: linear-gradient(144deg, ${props => props.$cardColors.topLeftColor} 0%, ${props => props.$cardColors.fiftyPercentColor} 50%, ${props => props.$cardColors.bottomRightColor} 100%);
    width: 300px;
    height: 475px;
  `,
  ContentWrapper: styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
  `,
  HehmDescription: styled.span`
    margin: 10px 0;
  `,
  BulletList: styled.ul`
    margin: 10px 0;
    padding-left: 20px;
  `,
  BulletItem: styled.li`
    margin: 5px 0;
  `,
  ProductLogo: styled.img<{ $imageHeight?: number }>`
    width: auto;
    height: ${props => props.$imageHeight ? `${props.$imageHeight}px` : 'auto'};
  `
}
